import React, { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { CandlestickSeries, createChart, createSeriesMarkers } from 'lightweight-charts';
import type { CandlestickData, IChartApi, ISeriesApi, PriceLineOptions, SeriesMarker, Time } from 'lightweight-charts';
import { PriceChange } from '@crypto-trading/common/browser';
import { TriggeredMarkerPrimitive, TRIGGER_MARKER_LABEL_SPACE_PX } from './TriggeredMarkerPrimitive';
import {
  CHART_INTERVAL_OPTIONS,
  Interval,
} from '@/hooks/usePositionPriceData';

interface PositionChartProps {
  priceChanges: PriceChange[];
  entryPrice?: number | null;
  exitPrice?: number | null;
  isProfitable?: boolean;
  openedAt: string | null | undefined;
  closedAt?: string | null;
  markerText?: string; // Text to show on the marker (default: "Opened")
  entryMarkerVariant?: 'arrow' | 'callout';
  onRequestOlderCandles?: () => void | Promise<void>;
  onRequestNewerCandles?: () => void | Promise<void>;
  hasMoreHistory?: boolean;
  hasMoreFuture?: boolean;
  onFollowLiveChange?: (followLive: boolean) => void;
  /** When this changes (e.g. interval), viewport state is reset instead of restored. */
  viewportResetKey?: string;
  title?: string;
  headerActions?: React.ReactNode;
  enabledIntervals?: Interval[];
  selectedInterval?: Interval;
  onIntervalChange?: (interval: Interval) => void;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  error?: { message: string } | null;
  onIntervalClick?: () => void;
  emptyMessage?: string;
}

export const PositionChart: React.FC<PositionChartProps> = ({
  priceChanges,
  entryPrice,
  exitPrice,
  isProfitable = false,
  openedAt,
  closedAt,
  markerText = 'Opened',
  entryMarkerVariant = 'arrow',
  onRequestOlderCandles,
  onRequestNewerCandles,
  hasMoreHistory = true,
  hasMoreFuture = true,
  onFollowLiveChange,
  viewportResetKey,
  title = 'Price Chart',
  headerActions,
  enabledIntervals = [],
  selectedInterval,
  onIntervalChange,
  isLoading = false,
  isLoadingMore = false,
  error = null,
  onIntervalClick,
  emptyMessage,
}) => {

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candlestickSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const lastLeftRequestAtRef = useRef<number>(0);
  const olderRequestInFlightRef = useRef(false);
  const viewportResetKeyRef = useRef(viewportResetKey);
  const previousViewportRef = useRef<{
    range: { from: number; to: number };
    firstTime: number;
    lastTime: number;
    length: number;
  } | null>(null);

  if (viewportResetKeyRef.current !== viewportResetKey) {
    viewportResetKeyRef.current = viewportResetKey;
    previousViewportRef.current = null;
  }

  useEffect(() => {
    if (!chartContainerRef.current || priceChanges.length === 0) return;

    // Create chart
    const CHART_HEIGHT = 300;
    const hasEntryPrice =
      typeof entryPrice === 'number' && Number.isFinite(entryPrice) && entryPrice > 0;
    const hasCalloutMarker =
      entryMarkerVariant === 'callout' && Boolean(openedAt) && hasEntryPrice;
    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: CHART_HEIGHT,
      layout: {
        background: { color: '#1a1b20' },
        textColor: '#717680',
      },
      grid: {
        vertLines: { color: '#2e2f35' },
        horzLines: { color: '#2e2f35' },
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: 'rgba(112, 132, 153, 0.2)',
      },
      rightPriceScale: {
        borderColor: 'rgba(112, 132, 153, 0.2)',
        visible: true,
        scaleMargins: {
          top: 0.05,
          bottom: 0.05,
        },
        entireTextOnly: false,
        autoScale: true,
        ticksVisible: true,
        invertScale: false,
      },
      leftPriceScale: {
        visible: false, // Hide left price scale to ensure right one is used
      },
      crosshair: {
        vertLine: {
          color: '#2e2f35',
          width: 2,
          style: 1,
          labelBackgroundColor: '#6ab2f2',
        },
        horzLine: {
          color: '#2e2f35',
          width: 2,
          style: 1,
          labelBackgroundColor: '#6ab2f2',
        },
      },
    });

    chartRef.current = chart;

    // Create candlestick series - explicitly attach to right price scale
    const candlestickSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#17b26a',
      downColor: '#d92d20',
      borderUpColor: '#17b26a',
      borderDownColor: '#d92d20',
      wickUpColor: '#17b26a',
      wickDownColor: '#d92d20',
      priceLineVisible: false,
      lastValueVisible: false,
      priceScaleId: 'right', // Explicitly attach to right price scale
    });

    candlestickSeriesRef.current = candlestickSeries;

    // Convert price changes to candlestick data
    const candleData: CandlestickData[] = priceChanges
      .map((candle) => ({
        time: (new Date(candle.startTime).getTime() / 1000) as Time,
        open: Number.parseFloat(candle.openPrice),
        high: Number.parseFloat(candle.highPrice),
        low: Number.parseFloat(candle.lowPrice),
        close: Number.parseFloat(candle.closePrice),
      }))
      .sort((a, b) => (a.time as number) - (b.time as number)); // Sort by time in ascending order

    candlestickSeries.setData(candleData);

    // Calculate price dynamics to adapt scale/formatting
    const prices = candleData.flatMap((candle) => [candle.open, candle.high, candle.low, candle.close]);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice;
    const avgPrice = (minPrice + maxPrice) / 2 || 1;
    const rangePercentage = avgPrice === 0 ? 0 : (priceRange / avgPrice) * 100;

    // Adaptive precision for small-price assets
    let precision = 2;
    if (avgPrice < 1 || rangePercentage < 2) {
      precision = 4;
    }
    if (avgPrice < 0.1 || rangePercentage < 0.5) {
      precision = 6;
    }
    const minMove = 1 / Math.pow(10, precision);

    candlestickSeries.applyOptions({
      priceFormat: {
        type: 'price',
        precision,
        minMove,
      },
    });

    // Adaptive scale margins to add breathing room for tight ranges
    let scaleMargins = { top: 0.05, bottom: 0.05 };
    if (rangePercentage < 0.5) {
      scaleMargins = { top: 0.18, bottom: 0.18 };
    } else if (rangePercentage < 1) {
      scaleMargins = { top: 0.12, bottom: 0.12 };
    } else if (rangePercentage < 2) {
      scaleMargins = { top: 0.08, bottom: 0.08 };
    }

    const referencePrices = [minPrice, maxPrice];
    if (hasEntryPrice) {
      referencePrices.push(entryPrice);
    }
    if (exitPrice) {
      referencePrices.push(exitPrice);
    }
    const scaleMin = Math.min(...referencePrices);
    const scaleMax = Math.max(...referencePrices);
    const scalePad = Math.max((scaleMax - scaleMin) * 0.08, minMove * 4);

    if (hasCalloutMarker) {
      const labelSpaceFraction = TRIGGER_MARKER_LABEL_SPACE_PX / CHART_HEIGHT;
      scaleMargins = {
        ...scaleMargins,
        bottom: Math.max(scaleMargins.bottom, labelSpaceFraction),
      };
    }

    candlestickSeries.applyOptions({
      autoscaleInfoProvider: () => ({
        priceRange: {
          minValue: scaleMin - scalePad,
          maxValue: scaleMax + scalePad,
        },
      }),
    });

    // Ensure price scale is visible and shows labels
    // This is especially important for opened positions without exit price
    const priceScale = chart.priceScale('right');
    priceScale.applyOptions({
      visible: true,
      entireTextOnly: false,
      autoScale: true,
      ticksVisible: true,
      invertScale: false,
      scaleMargins,
    });

    // Helper function to find the correct candle for a marker
    // Checks if the price is within the candle's range, otherwise checks left neighbor
    // Works with sorted candleData array
    const findCandleForMarker = (
      timestamp: number,
      targetPrice?: number,
    ): CandlestickData | null => {
      if (candleData.length === 0) return null;
      
      const targetTimeSeconds = timestamp / 1000; // Convert to seconds
      
      // Find the candle whose time is closest to but not after the target timestamp
      // Since candleData is sorted by time, we can search directly
      let candleIndex = -1;
      let closestTimeDiff = Infinity;
      
      for (let i = 0; i < candleData.length; i++) {
        const candleTime = candleData[i].time as number;
        const timeDiff = targetTimeSeconds - candleTime;
        
        // Find the candle that starts before or at the target time, and is closest
        if (timeDiff >= 0 && timeDiff < closestTimeDiff) {
          closestTimeDiff = timeDiff;
          candleIndex = i;
        }
      }
      
      // If no candle found before the target time, use the first candle
      if (candleIndex === -1) {
        candleIndex = 0;
      }
      
      if (candleIndex < 0 || candleIndex >= candleData.length) {
        return null;
      }
      
      const candle = candleData[candleIndex];

      if (targetPrice == null || !Number.isFinite(targetPrice)) {
        return candle;
      }
      
      // Check if the target price is within this candle's price range
      if (targetPrice >= candle.low && targetPrice <= candle.high) {
        return candle;
      }
      
      // If not, check the left neighbor (previous candle)
      if (candleIndex > 0) {
        const leftCandle = candleData[candleIndex - 1];
        if (targetPrice >= leftCandle.low && targetPrice <= leftCandle.high) {
          return leftCandle;
        }
      }
      
      // If neither candle contains the price, return the original candle
      return candle;
    };

    // Add price lines for entry and exit prices
    // Callout = trigger dot only; skip entry line to avoid a second horizontal + axis label
    if (hasEntryPrice && entryMarkerVariant !== 'callout') {
      const entryPriceLine: PriceLineOptions = {
        price: entryPrice,
        color: '#6ab2f2',
        lineWidth: 2,
        lineStyle: 0, // Solid line
        axisLabelVisible: true,
        title: ``,
        lineVisible: true,
        axisLabelColor: '#6ab2f2',
        axisLabelTextColor: 'white',
      };
      candlestickSeries.createPriceLine(entryPriceLine);
    }

    if (exitPrice) {
      const exitColor = isProfitable ? '#4ade80' : '#f87171';
      const exitPriceLine: PriceLineOptions = {
        price: exitPrice,
        color: exitColor,
        lineWidth: 2,
        lineStyle: 0, // Solid line
        axisLabelVisible: true,
        title: ``,
        lineVisible: true,
        axisLabelColor: exitColor,
        axisLabelTextColor: 'white',
      };
      candlestickSeries.createPriceLine(exitPriceLine);
    }

    // Add markers for position opened/closed.
    const markers: SeriesMarker<Time>[] = [];
    let entryCandleForViewport: CandlestickData | null = null;
    let exitCandleForViewport: CandlestickData | null = null;

    if (openedAt && candleData.length > 0) {
      const openedAtTimestamp = new Date(openedAt).getTime();
      const entryCandle = findCandleForMarker(
        openedAtTimestamp,
        hasEntryPrice ? entryPrice : undefined,
      );
      
      if (entryCandle) {
        entryCandleForViewport = entryCandle;
        if (hasEntryPrice) {
          if (entryMarkerVariant === 'callout') {
            const atPrice = candlestickSeries.priceFormatter().format(entryPrice);
            candlestickSeries.attachPrimitive(
              new TriggeredMarkerPrimitive({
                time: entryCandle.time,
                price: entryPrice,
                labelText: `Triggered at ${atPrice}`,
              }),
            );
          } else {
            markers.push({
              time: entryCandle.time,
              position: 'belowBar' as const,
              color: 'white',
              shape: 'arrowUp' as const,
              text: markerText,
            });
          }
        }
      }
    }

    if (exitPrice && closedAt && candleData.length > 0) {
      const closedAtTimestamp = new Date(closedAt).getTime();
      const exitCandle = findCandleForMarker(closedAtTimestamp, exitPrice);
      
      if (exitCandle) {
        exitCandleForViewport = exitCandle;
        markers.push({
          time: exitCandle.time,
          position: 'aboveBar' as const,
          color: 'white',
          shape: 'arrowDown' as const,
          text: `Closed`,
        });
      }
    }

    if (markers.length > 0) {
        createSeriesMarkers(candlestickSeries, markers);
    }

    const previousViewport = previousViewportRef.current;
    const firstTime = candleData[0]?.time as number | undefined;
    const lastTime = candleData[candleData.length - 1]?.time as number | undefined;
    let restoredViewport = false;

    if (
      previousViewport &&
      firstTime !== undefined &&
      lastTime !== undefined &&
      previousViewport.range.to < candleData.length &&
      previousViewport.range.from >= 0
    ) {
      const overlaps =
        previousViewport.lastTime >= firstTime &&
        previousViewport.firstTime <= lastTime;
      if (overlaps) {
        const isPrepend =
          previousViewport.lastTime === lastTime &&
          previousViewport.firstTime !== firstTime &&
          candleData.length > previousViewport.length;
        const shift = isPrepend ? candleData.length - previousViewport.length : 0;
        chart.timeScale().setVisibleLogicalRange({
          from: previousViewport.range.from + shift,
          to: previousViewport.range.to + shift,
        });
        restoredViewport = true;
      }
    }

    if (!restoredViewport) {
      const entryIndex = entryCandleForViewport
        ? candleData.findIndex((candle) => candle.time === entryCandleForViewport.time)
        : -1;
      const exitIndex = exitCandleForViewport
        ? candleData.findIndex((candle) => candle.time === exitCandleForViewport.time)
        : -1;
      const edgePadding = 5;

      if (entryIndex >= 0 && exitIndex >= 0 && exitIndex >= entryIndex) {
        chart.timeScale().setVisibleLogicalRange({
          from: Math.max(0, entryIndex - edgePadding),
          to: Math.min(candleData.length - 1, exitIndex + edgePadding),
        });
      } else if (entryIndex >= 0) {
        const visibleBars = Math.min(48, candleData.length);
        const barsBeforeEntry = Math.floor(visibleBars * 0.35);
        const from = Math.max(0, entryIndex - barsBeforeEntry);
        const to = Math.min(candleData.length - 1, from + visibleBars - 1);
        chart.timeScale().setVisibleLogicalRange({ from, to });
      } else {
        chart.timeScale().fitContent();
      }
    }
    
    // Force price scale to show labels by ensuring it's properly configured after all data is set
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      setTimeout(() => {
        try {
          const priceScaleAfter = chart.priceScale('right');
          
          // Force price scale to recalculate and show labels
          priceScaleAfter.applyOptions({
            visible: true,
            autoScale: true,
            ticksVisible: true,
            entireTextOnly: false,
            invertScale: false,
            scaleMargins,
          });
          
          // Try to force a resize to trigger label rendering
          if (chartContainerRef.current) {
            chart.applyOptions({
              width: chartContainerRef.current.clientWidth,
            });
          }
        } catch {
          // Silently handle errors accessing price scale - not critical for chart functionality
        }
      }, 100);
    });

    const handleLogicalRangeChange = (range: { from: number; to: number } | null) => {
      if (!range) return;
      const totalCandles = candleData.length;
      const roundedFrom = Math.floor(range.from);
      const nearLeftEdge = roundedFrom <= 12;
      const nearRightEdge = range.to >= totalCandles - 3;
      const canPaginateFuture = nearRightEdge && hasMoreFuture && Boolean(onRequestNewerCandles);
      onFollowLiveChange?.(nearRightEdge && !canPaginateFuture);

      if (!nearLeftEdge || !hasMoreHistory || !onRequestOlderCandles) {
        // continue to right-edge handling
      } else {
        const now = Date.now();
        if (!olderRequestInFlightRef.current && now - lastLeftRequestAtRef.current >= 800) {
          lastLeftRequestAtRef.current = now;
          const request = onRequestOlderCandles();
          if (request && typeof (request as Promise<void>).finally === 'function') {
            olderRequestInFlightRef.current = true;
            (request as Promise<void>).finally(() => {
              olderRequestInFlightRef.current = false;
            });
          }
        }
      }

      if (!nearRightEdge || !hasMoreFuture || !onRequestNewerCandles) {
        return;
      }
      const now = Date.now();
      if (olderRequestInFlightRef.current || now - lastLeftRequestAtRef.current < 800) {
        return;
      }
      lastLeftRequestAtRef.current = now;
      const request = onRequestNewerCandles();
      if (request && typeof (request as Promise<void>).finally === 'function') {
        olderRequestInFlightRef.current = true;
        (request as Promise<void>).finally(() => {
          olderRequestInFlightRef.current = false;
        });
      }
    };

    chart.timeScale().subscribeVisibleLogicalRangeChange(handleLogicalRangeChange);

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      const visibleRange = chart.timeScale().getVisibleLogicalRange();
      if (visibleRange && firstTime !== undefined && lastTime !== undefined) {
        previousViewportRef.current = {
          range: visibleRange,
          firstTime,
          lastTime,
          length: candleData.length,
        };
      }
      window.removeEventListener('resize', handleResize);
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(handleLogicalRangeChange);
      chart.remove();
    };
  }, [
    priceChanges,
    entryPrice,
    exitPrice,
    isProfitable,
    openedAt,
    closedAt,
    markerText,
    entryMarkerVariant,
    onRequestOlderCandles,
    onRequestNewerCandles,
    hasMoreHistory,
    hasMoreFuture,
    onFollowLiveChange,
    viewportResetKey,
  ]);

  const visibleIntervals = CHART_INTERVAL_OPTIONS.filter((interval) =>
    enabledIntervals.includes(interval),
  );
  const showIntervalSelector =
    Boolean(onIntervalChange) && visibleIntervals.length > 0 && selectedInterval;

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-text-primary">{title}</h3>
        <div className="flex items-center gap-2">
          {showIntervalSelector && (
            <div className="flex gap-1">
              {visibleIntervals.map((interval) => {
                const isSelected = selectedInterval === interval;
                return (
                  <button
                    key={interval}
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onIntervalClick?.();
                      onIntervalChange?.(interval);
                    }}
                    className={
                      'px-2 py-1 text-xs font-medium rounded transition-colors ' +
                      (isSelected
                        ? 'bg-gray-500 text-text-primary'
                        : 'bg-primary/50 text-text-secondary hover:bg-primary/70')
                    }
                  >
                    {interval}
                  </button>
                );
              })}
            </div>
          )}
          {headerActions}
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center h-[300px] bg-primary/50 rounded-lg">
          <Loader2 className="w-6 h-6 animate-spin text-accent" />
        </div>
      )}

      {!isLoading && error && (
        <div className="flex items-center justify-center h-[300px] bg-primary/50 rounded-lg px-4">
          <p className="text-text-secondary text-sm text-center">{error.message}</p>
        </div>
      )}

      {!isLoading && !error && priceChanges.length === 0 && emptyMessage && (
        <div className="flex items-center justify-center h-[300px] bg-primary/50 rounded-lg">
          <p className="text-text-secondary text-sm">{emptyMessage}</p>
        </div>
      )}

      {!isLoading && !error && priceChanges.length > 0 && (
        <div className="w-full rounded-xl bg-primary/50">
          <div
            ref={chartContainerRef}
            className="w-full rounded-xl"
            style={{ overflow: 'visible' }}
          />
        </div>
      )}

      {isLoadingMore && (
        <div className="flex items-center justify-center py-2">
          <Loader2 className="w-4 h-4 animate-spin text-accent" />
        </div>
      )}
    </div>
  );
};

